import {TieredMenu} from 'primereact/tieredmenu';
import {useMemo, useRef} from 'react';
import {url} from '@/Helpers/general';
import {getUserAvatar} from '@/Helpers/user';
import {Link, useLocation} from 'react-router-dom';
import {ExcelIcon} from '@/Helpers/svg-icons';
import {useAuth0} from "@auth0/auth0-react";
import {useAuthUser} from "@/Hooks/useAuthUser";
import {can} from "@/Util/permissionChecks";
import styled, {createGlobalStyle} from "styled-components";
import {useQueryClient} from "@tanstack/react-query";
import {OverlayPanel} from "primereact/overlaypanel";
import {useGetNotifications} from "@/Service/Api/V1/ApiHooks/Notification/useGetNotifications";
import {Divider} from "primereact/divider";
import {Button} from "primereact/button";
import {Menu} from "primereact/menu";
import {QueryKeys} from "@/Service/Api/V1/QueryKeys/QueryKeys";
import {Badge} from "primereact/badge";
import {useMarkNotificationAsRead} from "@/Service/Api/V1/ApiHooks/Notification/useMarkNotificationAsRead";
import {useGetNotificationsUnreadCount} from "@/Service/Api/V1/ApiHooks/Notification/useGetNotificationsUnreadCount";
import {formatToUKDate} from "@/Util/formatToUKDate";
import {NotificationMarkedAsReadMessage} from "@/Messages/Toast/Notifications/NotificationMarkedAsReadMessage";
import {useAddToast} from "@/Hooks/useAddToast";
import {Building, PoundSterling, User} from 'lucide-react';
import {CustomErrorMessage} from "@/Messages/Toast/General/CustomErrorMessage";
import {ZodIssue} from "zod";

const StyledWrap = styled.div`
    .notification-icon {
        color: var(--gray-500);
        font-size: 1.3rem;
        cursor: pointer;
    }

    .notification-badge {
        background: var(--primary-color);
        min-width: 1.3rem;
        height: 1.3rem;
        line-height: 1.3rem;
    }
`;

const NotificationsStyle = createGlobalStyle`
    .notifications {
        max-height: 50rem;
        overflow-y: auto;
        padding-left: 15px;
        padding-right: 15px;


        &__list {
            list-style: none;
            padding: 0;
            margin-bottom: 20px;
        }

        &__item:not(:first-child) {
            margin-top: 0.5rem;
        }
    }
`;

const Header = () => {
    const queryClient = useQueryClient();

    const notificationOverlayRef = useRef<OverlayPanel>(null);
    const menuRef = useRef<Menu>(null);
    const location = useLocation();

    const {logout: auth0Logout} = useAuth0();
    const authUser = useAuthUser();
    const addToast = useAddToast();

    const {
        data: notificationsData,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useGetNotifications();

    const {data: notificationsUnreadCount} = useGetNotificationsUnreadCount();
    const {mutateAsync: markAsRead, isPending: isMarkAsReadPending} = useMarkNotificationAsRead();

    const markNotificationAsRead = async (notificationId: string) => {
        await markAsRead(
            {
                notification_id: notificationId
            },
            {
                onSuccess: (data) => {
                    addToast(NotificationMarkedAsReadMessage);
                    queryClient.invalidateQueries({...QueryKeys.notifications.list()});
                    queryClient.invalidateQueries({...QueryKeys.notifications.unreadCount});
                },
                onError: (error: Error | ZodIssue) => {
                    addToast(CustomErrorMessage(error));
                }
            }
        );
    };

    /**
     * Check if the current url path matched the given one
     *
     * @param {string} path an url path
     *
     * @returns {boolean}
     */
    const checkUrlPath = (path: string) => {
        return location && location?.pathname === '/' + path;
    };

    const userMenuItems = useMemo(() => (
        [{
            className: 'content-item',
            template: () =>
                <div className="user-info p-2 flex">
                    {getUserAvatar(authUser)}

                    <div className="mt-1 p-1">
                        <div className="user-name">{authUser?.name}</div>
                    </div>
                </div>
        },
            {
                separator: true
            },
            {
                label: 'My Profile',
                template: () => <Link className="p-menuitem-link" to={url('my-profile')}>
                    My Profile
                </Link>
            },
            {
                separator: true
            },
            {
                label: 'Logout',
                command: () => {
                    logout();
                }
            }
        ]
    ), [authUser]);

    /**
     * Logout the user and refresh the page
     */
    const logout = async () => {
        await auth0Logout();
    };

    return (
        <StyledWrap>
            <div className="main-header">
                <div className="flex justify-content-between w-full">
                    <Link className="site-logo uppercase-dark-text p-link flex my-3" to={url('home')}>
                        <img src="/img/nightingale-logo.png" alt={'nightingale-logo'}/>
                    </Link>

                    <div className={'flex align-items-center gap-3'}>
                        <NotificationsStyle/>
                        <div
                            className={'my-auto'}
                            onClick={(e) => notificationOverlayRef.current?.toggle(e, null)}
                        >
                            <i className="notification-icon pi pi-bell p-overlay-badge">
                                {
                                    notificationsUnreadCount
                                        ? <Badge value={notificationsUnreadCount}
                                                 className="notification-badge"></Badge> : null
                                }
                            </i>
                        </div>
                        <OverlayPanel ref={notificationOverlayRef}>
                            <div className={'flex flex-column gap-1'}>
                                <div>
                                    <h4 className="m-0">Notifications</h4>
                                    <Divider className={'mb-0'}/>
                                </div>
                                <div className="notifications">
                                    <ul className="notifications__list">
                                        {
                                            notificationsData
                                            && notificationsData.pages.map(page => {
                                                return page.data.map((notification) => {
                                                    return <li
                                                        key={notification.id}
                                                        className="notifications__item"
                                                    >
                                                        <div className="flex justify-content-between gap-2">
                                                            <div>
                                                                <p className="m-0">{notification.body}</p>
                                                                <small>{formatToUKDate(notification.created_at, true)}</small>
                                                            </div>
                                                            {
                                                                !notification.read_at
                                                                &&
                                                                <Button
                                                                    className={'h-2rem p-button-rounded'}
                                                                    loading={isMarkAsReadPending}
                                                                    onClick={() => markNotificationAsRead(notification.id)}
                                                                    title={'Read'}
                                                                >Read
                                                                </Button>

                                                            }
                                                        </div>
                                                    </li>;
                                                });
                                            })
                                        }
                                    </ul>

                                </div>
                                {
                                    hasNextPage && <div className={'mx-auto'}>
                                        <Button
                                            label="Load more"
                                            className={'p-button-rounded'}
                                            loading={isFetchingNextPage || isMarkAsReadPending}
                                            onClick={async () => {
                                                await fetchNextPage();
                                            }}
                                        />
                                    </div>
                                }
                            </div>
                        </OverlayPanel>
                        <div
                            className="user-info-wrap flex align-items-center"
                            onMouseOver={(e) => {
                                menuRef.current.show(e);
                            }}
                            onMouseOut={(e) => {
                                menuRef.current.hide(e);
                            }}
                        >
                            {getUserAvatar(authUser)}

                            <div className="user-info">
                                <div>{authUser?.name}</div>
                                <span className="p-link font-bold" onClick={logout}>
                                    Logout
                                </span>
                            </div>

                            <TieredMenu className="w-15rem" model={userMenuItems} popup ref={menuRef}/>
                        </div>
                    </div>
                </div>

                <ul className="header-buttons-menu">
                    <li>
                        <Link className={checkUrlPath('') ? 'active' : ''} to={url('home')}>
                            <i className="pi pi-home"></i> Dashboard
                        </Link>
                    </li>
                    <li>
                        <Link className={checkUrlPath('new-claim') ? 'active' : ''} to={url('new-claim')}>
                            <i className="pi pi-plus"></i> New Claim
                        </Link>
                    </li>
                    <li>
                        <Link className={checkUrlPath('reports') ? 'active' : ''} to={url('reports')}>
                            <ExcelIcon size="16" color1="#4f555a" color2="#6c757d"/> Reports
                        </Link>
                    </li>
                    {can('access:supervisor') && (
                        <li>
                            <Link className={checkUrlPath('dsar-request') ? 'active' : ''} to={url('dsar-request')}>
                                <i className="pi pi-database"></i> DSAR Request
                            </Link>
                        </li>
                    )}
                    {can('read:audit') && (
                        <li>
                            <Link className={checkUrlPath('audits') ? 'active' : ''} to={url('audits')}>
                                <i className="pi pi-history"></i> Audits
                            </Link>
                        </li>
                    )}
                    {can('read:client') && (
                        <li>
                            <Link className={checkUrlPath('clients') ? 'active' : ''} to={url('clients')}>
                                <i className="pi pi-users"></i> Clients
                            </Link>
                        </li>
                    )}
                    {can('read:team') && (
                        <li>
                            <Link className={checkUrlPath('teams') ? 'active' : ''} to={url('teams')}>
                                <Building size="16"/> Teams
                            </Link>
                        </li>
                    )}
                    {can('read:user') && (
                        <li>
                            <Link className={checkUrlPath('users') ? 'active' : ''} to={url('users')}>
                                <User size="16"/> Users
                            </Link>
                        </li>
                    )}
                    {can('create:gta') && (
                        <li>
                            <Link className={checkUrlPath('gta-rates') ? 'active' : ''} to={url('gta-rates')}>
                                <PoundSterling size="16"/> GTA Rates
                            </Link>
                        </li>
                    )}
                </ul>
            </div>
        </StyledWrap>
    );
};

export default Header;
